import React, { useState, useEffect } from 'react';
import { FiArrowUpRight, FiArrowDown, FiDownload, FiSun, FiMoon, FiMapPin, FiMail, FiPhone, FiTerminal, FiGitBranch } from 'react-icons/fi';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import dataNL from './data.json';
import dataEN from './data-en.json';
import CareerTimeline from './components/CareerTimeline';
import YearScrubber from './components/YearScrubber';
import SkillsExplorer from './components/SkillsExplorer';
import './App.css';

function savedPreference(key, fallback) {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
}

export function Prompt({ children }) {
  return <div className="prompt"><span className="prompt-user">jelmer</span><span className="prompt-path">~</span><span className="prompt-chevron">❯</span><span>{children}</span></div>;
}

const App = () => {
  const [language, setLanguage] = useState(() => savedPreference('lang', 'nl') === 'en' ? 'en' : 'nl');
  const [darkMode, setDarkMode] = useState(() => savedPreference('theme', 'dark') === 'dark');
  const data = language === 'nl' ? dataNL : dataEN;
  const en = language === 'en';
  const t = (english, dutch) => en ? english : dutch;
  const currentWork = data.work.find(item => !item.endDate);

  useEffect(() => {
    document.documentElement.lang = language;
    try { localStorage.setItem('lang', language); } catch {}
  }, [language]);
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    try { localStorage.setItem('theme', darkMode ? 'dark' : 'light'); } catch {}
  }, [darkMode]);

  return (
    <div className="site-shell">
      <a href="#main" className="skip-link">{t('Skip to content', 'Ga naar inhoud')}</a>
      <header className="site-header">
        <div className="header-inner">
          <a className="wordmark" href="#hero" aria-label="Jelmer Franken — home"><img className="brand-icon" src="/jf-icon.svg" width="38" height="38" alt="" /><span>jelmer<span className="muted">.sh</span></span></a>
          <nav aria-label={t('Main navigation', 'Hoofdnavigatie')}>
            <a href="#journey"><span>01</span> {t('Journey', 'Loopbaan')}</a>
            <a href="#skills"><span>02</span> {t('Skills', 'Skills')}</a>
          </nav>
          <div className="header-actions">
            <button className="language-button" onClick={() => setLanguage(en ? 'nl' : 'en')} aria-label={t('Switch to Dutch', 'Switch to English')}>{language.toUpperCase()} <span>⌄</span></button>
            <button className="icon-button" onClick={() => setDarkMode(!darkMode)} aria-label={t(darkMode ? 'Use light theme' : 'Use dark theme', darkMode ? 'Licht thema gebruiken' : 'Donker thema gebruiken')}>{darkMode ? <FiSun /> : <FiMoon />}</button>
          </div>
        </div>
      </header>

      <main id="main" className="page-container">
        <section id="hero" className="hero">
          <div className="hero-copy">
            <Prompt>whoami</Prompt>
            <h1>Jelmer<br />Franken<span className="name-period">.</span></h1>
            <p className="hero-role">{currentWork.position}<br /><span className="muted">@ </span><a href={currentWork.url} target="_blank" rel="noreferrer">{currentWork.name} <FiArrowUpRight /></a></p>
            <p className="hero-summary">{data.basics.summary}</p>
            <div className="hero-actions">
              <a className="button button-primary" href="/Jelmer Franken - CV - 2024.pdf" download><FiDownload /> Download CV</a>
              <a className="button button-secondary" href={`mailto:${data.basics.email}`}>{t('Get in touch', 'Neem contact op')} <FiArrowUpRight /></a>
            </div>
            <div className="hero-location"><FiMapPin /> {data.basics.location.city}, {t('Netherlands', 'Nederland')}<span className="small-divider" />{data.basics.profiles.map(profile => <a key={profile.network} href={profile.url} target="_blank" rel="noreferrer" aria-label={profile.network}>{profile.network === 'GitHub' ? <FaGithub /> : <FaLinkedin />}</a>)}</div>
          </div>
          <div className="hero-terminal terminal-window">
            <div className="window-bar"><div className="traffic-lights" aria-hidden="true"><i /><i /><i /></div><span>jelmer@portfolio: ~</span><FiTerminal aria-hidden="true" /></div>
            <div className="terminal-body">
              <Prompt>fastfetch</Prompt>
              <div className="fetch-profile">
                <img src={data.basics.image} alt={data.basics.name} width="104" height="104" />
                <div><span className="fetch-name">jelmer@portfolio</span><div className="fetch-rule">─────────────────</div><span className="muted">{t('Infrastructure. Automation.', 'Infrastructuur. Automatisering.')}<br />{t('A little curiosity, a lot of Linux.', 'Een beetje nieuwsgierig. Veel Linux.')}</span></div>
              </div>
              <dl className="fetch-details">
                <div><dt>{t('Role', 'Rol')}</dt><dd>{currentWork.position}</dd></div>
                <div><dt>{t('Company', 'Bedrijf')}</dt><dd>{currentWork.name}</dd></div>
                <div><dt>{t('Focus', 'Focus')}</dt><dd>Linux / Ansible / Docker</dd></div>
                <div><dt>{t('Homelab', 'Homelab')}</dt><dd>Proxmox + Home Assistant</dd></div>
                <div><dt>{t('Location', 'Locatie')}</dt><dd>{data.basics.location.city}, NL</dd></div>
              </dl>
              <div className="terminal-palette" aria-hidden="true">{['#252932', '#e95470', '#91b68a', '#e2bb79', '#7b9fbe', '#b49fc2', '#83b8b0', '#e6e7eb'].map(color => <span key={color} style={{ background: color }} />)}</div>
              <div className="terminal-comment"># {t('Always something running in the homelab.', 'Er draait altijd iets in het homelab.')}</div>
              <div className="idle-prompt" aria-hidden="true"><Prompt><span className="cursor" /></Prompt></div>
            </div>
            <div className="terminal-status"><span><span className="status-dot" /> zsh</span><span>~/portfolio <FiGitBranch /> main</span></div>
          </div>
          <div className="hero-bottom"><span>LINUX · INFRASTRUCTURE · AUTOMATION</span><a href="#journey">{t('Explore my journey', 'Ontdek mijn loopbaan')} <FiArrowDown /></a></div>
        </section>

        <section id="journey" className="content-section">
          <div className="section-heading"><div><p className="section-command"><span>01 /</span> history</p><h2>{t('The journey so far', 'Mijn loopbaan tot nu toe')}<span>.</span></h2></div><span className="section-note">{t('Work, learning & everything between', 'Werk, studie & alles daartussen')}</span></div>
          <div className="journey-layout"><YearScrubber work={data.work} internships={data.internships} education={data.education} language={language} /><CareerTimeline work={data.work} internships={data.internships} education={data.education} projects={data.projects} language={language} /></div>
        </section>
        <section id="skills" className="content-section">
          <div className="section-heading"><div><p className="section-command"><span>02 /</span> cat jelmerfranken.nl/main.yml</p><h2>{t('What I bring', 'Wat ik meebreng')}<span>.</span></h2></div><span className="section-note">{t('Work · Skills · Interests', 'Werk · Skills · Interesses')}</span></div>
          <SkillsExplorer language={language} />
        </section>
        <footer className="site-footer">
          <Prompt>exit</Prompt>
          <div className="footer-main"><div><h2>{t('Let’s connect', 'Laten we kennismaken')}<span>.</span></h2><a className="contact-link" href={`mailto:${data.basics.email}`}>{data.basics.email} <FiArrowUpRight /></a>{data.basics.phone && /\d{6}/.test(data.basics.phone.replace(/\s/g, '')) && <a className="phone-link" href={`tel:${data.basics.phone}`}><FiPhone /> {data.basics.phone}</a>}</div><div className="footer-links">{data.basics.profiles.map(profile => <a key={profile.network} href={profile.url} target="_blank" rel="noreferrer">{profile.network} <FiArrowUpRight /></a>)}<a href={`mailto:${data.basics.email}`}><FiMail /> Email</a></div></div>
          <div className="footer-bottom"><span>© {new Date().getFullYear()} {data.basics.name}</span><a href="#hero">cd ~ <span>↑</span></a><span>{t('End of file. Not the journey.', 'Einde bestand. Niet de loopbaan.')}</span></div>
        </footer>
      </main>
    </div>
  );
};
export default App;
